// Generado por la división del monolito.
import { useState, useRef, useEffect, hashPin, Ic, roleAvatarClass, roleLabel } from './common.jsx';

export function UserSwitcher({users, activeUser, onSwitch, onLogout}) {
  const [open, setOpen] = useState(false);
  const [pinTarget, setPinTarget] = useState(null); // user to switch to
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const pinRef = useRef(null);

  if (!activeUser) return null;

  const startSwitch = u => {
    if (u.id === activeUser.id) { setOpen(false); return; }
    setPinTarget(u); setPin(''); setErr(''); setOpen(false);
    setTimeout(()=>pinRef.current?.focus(), 50);
  };

  const confirmSwitch = async () => {
    if (pin.length < 4) return;
    if (!pinTarget?.email) { setErr('El usuario destino no tiene email — no se puede cambiar de sesión'); return; }
    setLoading(true); setErr('');
    // lote-92: enviamos el pin en claro y el email del usuario target. El
    // server crea una nueva cookie de sesión, machacando la previa.
    const r = await fetch('/api/auth/login',{method:'POST',headers:{'Content-Type':'application/json'},
      body:JSON.stringify({email: pinTarget.email, pin})});
    if (r.ok) { const u = await r.json(); onSwitch(u); setPinTarget(null); }
    else { setErr('Contraseña incorrecta'); setPin(''); pinRef.current?.focus(); }
    setLoading(false);
  };

  return (
    <div className="usw">
      {/* PIN modal for switching */}
      {pinTarget && (
        <div className="ov" onClick={()=>setPinTarget(null)}>
          <div className="modal ani" style={{maxWidth:340}} onClick={e=>e.stopPropagation()}>
            <div className="mh"><h3>Cambiar a {pinTarget.name}</h3>
              <button className="btn btn-gh btn-ic" onClick={()=>setPinTarget(null)}>{Ic.close}</button></div>
            <div className="mb" style={{textAlign:'center'}}>
              <div className={`uav ${roleAvatarClass(pinTarget.role)}`} style={{width:48,height:48,fontSize:20,margin:'0 auto 12px'}}>{pinTarget.name[0].toUpperCase()}</div>
              <p style={{fontSize:13,color:'var(--tx2)',marginBottom:16}}>Introduce la contraseña de <strong>{pinTarget.name}</strong></p>
              <input ref={pinRef} type="password" maxLength={64} value={pin}
                onChange={e=>{ setPin(e.target.value); setErr(''); }}
                onKeyDown={e=>e.key==='Enter'&&confirmSwitch()}
                placeholder="••••••••"
                autoComplete="current-password"
                style={{width:'100%',border:`1.5px solid ${err?'var(--rd)':'var(--bd)'}`,borderRadius:'var(--r)',padding:'12px',fontSize:16,textAlign:'center',fontFamily:'inherit',outline:'none'}}
              />
              {err && <div style={{color:'var(--rd)',fontSize:12,marginTop:6}}>{err}</div>}
            </div>
            <div className="mf">
              <button className="btn btn-gh" onClick={()=>setPinTarget(null)}>Cancelar</button>
              <button className="btn btn-pu" onClick={confirmSwitch} disabled={loading||pin.length<4}>
                {loading?'…':'Entrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="usw-cur" onClick={()=>setOpen(o=>!o)}>
        <div className={`uav ${roleAvatarClass(activeUser.role)}`}>{activeUser.name[0].toUpperCase()}</div>
        <div><div className="usw-name">{activeUser.name}</div>
          <div className="usw-role">{roleLabel(activeUser.role)}</div>
        </div>
        <svg className="usw-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
      </div>
      {open && (
        <UserSwitcherDropdown
          users={users}
          activeUser={activeUser}
          onStartSwitch={startSwitch}
          onLogout={() => { setOpen(false); onLogout(); }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

// Dropdown extraído: con buscador (cuando hay > 6 users), lista scrolleable
// limitada a max-height + botón "Cerrar sesión" SIEMPRE visible al fondo
// (sticky), separado visualmente para que nunca quede oculto bajo scroll.
function UserSwitcherDropdown({ users, activeUser, onStartSwitch, onLogout, onClose }) {
  const [q, setQ] = useState('');
  const showSearch = (users || []).length > 6;
  const norm = s => (s||'').toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
  const filtered = q
    ? (users||[]).filter(u => norm(u.name).includes(norm(q)) || norm(u.email||'').includes(norm(q)))
    : (users||[]);
  // El user activo siempre primero, el resto ordenado alfabéticamente.
  const sorted = [...filtered].sort((a, b) => {
    if (a.id === activeUser.id) return -1;
    if (b.id === activeUser.id) return 1;
    return (a.name||'').localeCompare(b.name||'');
  });

  // ── DROPUP — el switcher vive al fondo del sidebar, así que el dropdown se
  // abre HACIA ARRIBA. Position absolute con bottom = 100% del padre + un
  // pequeño gap. El padre .usw tiene position relative implícito al añadirlo.
  // "Cerrar sesión" arriba del todo (lo PRIMERO que ve el usuario al abrir).
  return (
    <div className="usw-drop" style={{
      position:'absolute',
      bottom:'100%', left:10, right:10,
      marginBottom:6,
      display:'flex', flexDirection:'column',
      maxHeight:'min(70vh, 540px)',
      padding:0,
      boxShadow:'0 -8px 24px rgba(0,0,0,.32)',
      border:'1px solid rgba(255,255,255,.08)',
      zIndex:50
    }}>
      {/* "Cerrar sesión" PRIMERO — es el item con mayor intent en un dropdown
          de cuenta. Fondo rojo tenue + borde inferior para separar de la lista. */}
      <div
        className="usw-item"
        onClick={onLogout}
        style={{
          borderBottom:'1px solid rgba(255,255,255,.12)',
          background:'rgba(223,32,32,.10)',
          flexShrink:0
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="var(--rd)" strokeWidth="2" style={{width:16,height:16}}>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
        </svg>
        <div>
          <div className="usw-item-n" style={{color:'var(--rd)', fontWeight:700}}>Cerrar sesión</div>
        </div>
      </div>

      {showSearch && (
        <div style={{ padding:'8px 10px', borderBottom:'1px solid rgba(255,255,255,.08)', flexShrink:0 }}>
          <input
            autoFocus
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape') onClose(); }}
            placeholder={`Buscar entre ${users.length} usuarios…`}
            style={{
              width:'100%', padding:'7px 10px', fontSize:12,
              background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.1)',
              color:'#fff', borderRadius:6, outline:'none', boxSizing:'border-box'
            }}
          />
        </div>
      )}

      <div style={{ flex:1, overflowY:'auto', minHeight:0 }}>
        {sorted.length === 0 && (
          <div style={{ padding:'18px 14px', fontSize:12, color:'rgba(255,255,255,.5)', textAlign:'center' }}>
            Sin resultados
          </div>
        )}
        {sorted.map(u => (
          <div key={u.id} className={`usw-item ${u.id===activeUser.id?'active':''}`} onClick={() => onStartSwitch(u)}>
            <div className={`uav ${roleAvatarClass(u.role)}`}>{(u.name||'?')[0].toUpperCase()}</div>
            <div style={{minWidth:0, flex:1}}>
              <div className="usw-item-n" style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{u.name}</div>
              <div className="usw-item-r">{roleLabel(u.role)}</div>
            </div>
            {u.id===activeUser.id && (
              <svg viewBox="0 0 24 24" fill="none" stroke="var(--pu-l)" strokeWidth="2.5" style={{width:14,height:14,marginLeft:'auto',flexShrink:0}}>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
